import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import useAppStore from '../../store/appStore';
import * as api from '../../api/client';

const ALL_SUGGESTIONS = [
  'Should I join SingularityNET?',
  'How are my goals progressing?',
  'What are my key skills?',
  'I had a great meeting with Ibby about ASI:Mind today',
  'My sleep was only 5 hours last night',
  '/status',
  'What decisions am I facing?',
  'How is my career trending?',
  'What habits should I build?',
  'What did I learn this week?',
  'Summarise my relationships',
  'What are my biggest risks right now?',
  'How aligned am I with my long-term goals?',
  'What should I focus on today?',
  'I finished reading a book on AGI today',
];

const SUGGESTIONS_VISIBLE = 5;

export default function QueryPanel() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [usedSuggestions, setUsedSuggestions] = useState(new Set());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const messages = useAppStore(s => s.messages);
  const addMessage = useAppStore(s => s.addMessage);
  const appendToken = useAppStore(s => s.appendToken);
  const updateLastMessage = useAppStore(s => s.updateLastMessage);
  const sessionId = useAppStore(s => s.sessionId);
  const setSessionId = useAppStore(s => s.setSessionId);
  const setLastReasoningChain = useAppStore(s => s.setLastReasoningChain);
  const loadStats = useAppStore(s => s.loadStats);

  // Initialize session ID on first load
  useEffect(() => {
    if (!sessionId) {
      setSessionId(crypto.randomUUID());
    }
  }, [sessionId, setSessionId]);

  const sendMessage = useCallback(async (text) => {
    const msg = text.trim();
    if (!msg || loading) return;

    addMessage({ role: 'user', content: msg, ts: Date.now() });
    setInput('');
    setLoading(true);

    try {
      // Seed an empty assistant message — tokens will fill it in
      addMessage({ role: 'assistant', content: '', intent: '', pipeline: [], ts: Date.now() });

      for await (const event of api.sendMessageStream(msg, sessionId)) {
        if (event.type === 'pipeline_stage') {
          updateLastMessage(prev => ({
            pipeline: [...(prev.pipeline || []), event.stage],
          }));
        } else if (event.type === 'token') {
          appendToken(event.token);
        } else if (event.type === 'done') {
          if (!sessionId && event.session_id) setSessionId(event.session_id);
          updateLastMessage({
            intent: event.intent,
            reasoning: event.reasoning,
            sufficiency: event.sufficiency,
            pipeline: event.pipeline,
          });
          if (event.reasoning?.reasoning_chain) {
            setLastReasoningChain(event.reasoning.reasoning_chain);
          }
          if (event.atoms_written > 0) await loadStats();
        }
      }
    } catch (err) {
      updateLastMessage({
        content: `Connection error: ${err.message}. Is the backend running on port 8000?`,
        intent: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId, addMessage,
      appendToken, updateLastMessage, setSessionId, setLastReasoningChain, loadStats]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="query-panel">
      {/* Message history */}
      <div className="messages-container">
        {messages.length === 0 && (
          <WelcomeScreen
            suggestions={ALL_SUGGESTIONS.filter(q => !usedSuggestions.has(q)).slice(0, SUGGESTIONS_VISIBLE)}
            onSuggest={(q) => { setUsedSuggestions(prev => new Set([...prev, q])); setInput(q); inputRef.current?.focus(); }}
          />
        )}
        {messages.map((msg, i) => (
          <MessageBubble
            key={i}
            message={msg}
            isLast={i === messages.length - 1}
            suggestions={ALL_SUGGESTIONS.filter(q => !usedSuggestions.has(q)).slice(0, SUGGESTIONS_VISIBLE)}
            onSuggest={(q) => { setUsedSuggestions(prev => new Set([...prev, q])); setInput(q); inputRef.current?.focus(); }}
          />
        ))}
        {loading && messages[messages.length - 1]?.content === '' && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="input-area">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask anything about your life, or tell me something to remember..."
          rows={2}
          disabled={loading}
          className="chat-input"
        />
        <div className="input-controls">
          <button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className="send-button"
          >
            {loading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WelcomeScreen({ suggestions, onSuggest }) {
  return (
    <div className="welcome-screen">
      <div className="welcome-logo">ASI:Mind</div>
      <div className="welcome-tagline">know yourself</div>
      <p className="welcome-desc">
        A personal AI that reasons over your life — powered by OpenCog Hyperon DAS.
        Every fact you share is stored in your personal knowledge graph and reasoned over, not just retrieved.
      </p>
      <div className="suggestions">
        <div className="suggestions-label">Try asking:</div>
        {suggestions.map(q => (
          <button key={q} className="suggestion-chip" onClick={() => onSuggest(q)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

const STAGE_META = {
  'nl_parser.parse()':      { color: '#10b981', icon: '⌨', lib: 'GPT-OSS' },
  'metta_engine.run_query()': { color: '#0ea5e9', icon: '⚛', lib: 'Hyperon MeTTa' },
  'nl_formatter.format()':  { color: '#10b981', icon: '💬', lib: 'GPT-OSS' },
  'conv_ingest.ingest()':   { color: '#6366f1', icon: '🕸', lib: 'AtomSpace DAS' },
  'atom_api.get()':         { color: '#6366f1', icon: '🕸', lib: 'AtomSpace DAS' },
  'atom_api.stats()':       { color: '#6366f1', icon: '🕸', lib: 'AtomSpace DAS' },
  'atom_api.delete()':      { color: '#f59e0b', icon: '🗑', lib: 'AtomSpace DAS' },
  'atom_api.get() fallback':{ color: '#f59e0b', icon: '🕸', lib: 'AtomSpace DAS' },
};

function PipelineTrace({ pipeline, reasoning, intent }) {
  const [expanded, setExpanded] = useState(true);

  // Build stages: use backend pipeline if present, else infer from reasoning fields
  const stages = pipeline?.length > 0 ? pipeline : (() => {
    const s = [];
    s.push({ stage: 'nl_parser.parse()', label: 'NL → ParsedInput', active: true,
      detail: `intent=${intent}` });
    if (reasoning?.metta_query) {
      s.push({ stage: 'metta_engine.run_query()', label: 'MeTTa inference', active: true,
        detail: reasoning.metta_query });
    } else {
      s.push({ stage: 'atom_api.get()', label: 'AtomSpace read', active: true, detail: 'direct lookup' });
    }
    s.push({ stage: 'nl_formatter.format()', label: 'NL response', active: true, detail: '' });
    return s;
  })();

  return (
    <div className="pipeline-trace">
      <button className="pipeline-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="pipeline-toggle-label">Pipeline</span>
        <span className="pipeline-flow-mini">
          {stages.map((s, i) => {
            const meta = STAGE_META[s.stage] || { color: '#475569', icon: '·' };
            return (
              <span key={i} className="pipeline-mini-node" style={{ color: meta.color }}>
                {i > 0 && <span className="pipeline-mini-arrow">→</span>}
                <span className="pipeline-mini-icon">{meta.icon}</span>
              </span>
            );
          })}
        </span>
        <span className="pipeline-toggle-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="pipeline-stages">
          {stages.map((s, i) => {
            const meta = STAGE_META[s.stage] || { color: '#475569', icon: '·', lib: '' };
            return (
              <div key={i} className="pipeline-stage-row">
                {i > 0 && (
                  <div className="pipeline-connector">
                    <div className="pipeline-connector-line" style={{ borderColor: meta.color }} />
                    <div className="pipeline-connector-arrow" style={{ color: meta.color }}>▼</div>
                  </div>
                )}
                <div className="pipeline-stage" style={{ borderColor: meta.color }}>
                  <div className="pipeline-stage-header">
                    <span className="pipeline-stage-icon">{meta.icon}</span>
                    <code className="pipeline-stage-fn" style={{ color: meta.color }}>{s.stage}</code>
                    <span className="pipeline-stage-lib">{meta.lib}</span>
                  </div>
                  <div className="pipeline-stage-label">{s.label}</div>
                  {s.detail && (
                    <div className="pipeline-stage-detail">{s.detail}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message, isLast, suggestions, onSuggest }) {
  const [showChain, setShowChain] = useState(false);
  const isUser = message.role === 'user';
  const hasChain = message.reasoning?.reasoning_chain?.length > 0;
  const confidence = message.reasoning?.confidence;

  return (
    <div className={`message-bubble ${isUser ? 'user' : 'assistant'}`}>
      {/* SingularityNET pipeline trace */}
      {!isUser && (message.pipeline?.length > 0 || message.reasoning) && (
        <PipelineTrace pipeline={message.pipeline} reasoning={message.reasoning} intent={message.intent} />
      )}

      <div className="message-content">
        {isUser ? (
          <span>{message.content}</span>
        ) : (
          <ReactMarkdown>{message.content}</ReactMarkdown>
        )}
      </div>

      {/* Reasoning transparency — F-11 */}
      {!isUser && (hasChain || confidence) && (
        <div className="message-meta">
          {confidence !== undefined && confidence > 0 && (
            <span className="confidence-badge">
              {(confidence * 100).toFixed(0)}% confidence
            </span>
          )}
          {hasChain && (
            <button
              className="chain-toggle"
              onClick={() => setShowChain(!showChain)}
            >
              {showChain ? 'Hide reasoning' : 'Why?'}
            </button>
          )}
        </div>
      )}

      {showChain && message.reasoning?.reasoning_chain && (
        <div className="reasoning-chain">
          <div className="chain-title">Reasoning chain:</div>
          {message.reasoning.reasoning_chain.map((step, i) => (
            <div key={i} className="chain-step">
              <span className="chain-step-num">{i + 1}</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      )}

      {/* Data sufficiency warning */}
      {!isUser && message.sufficiency?.missing_data?.length > 0 && (
        <div className="sufficiency-warning">
          ⚠ Limited data: {message.sufficiency.missing_data.join(', ')}
        </div>
      )}

      <div className="message-ts">
        {new Date(message.ts).toLocaleTimeString()}
        {message.intent && message.intent !== 'unknown' && (
          <span className="intent-badge">{message.intent}</span>
        )}
      </div>

      {!isUser && isLast && suggestions?.length > 0 && (
        <div className="post-reply-suggestions">
          <div className="suggestions-label">Try asking:</div>
          <div className="suggestions-chips">
            {suggestions.map(q => (
              <button key={q} className="suggestion-chip" onClick={() => onSuggest(q)}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="message-bubble assistant">
      <div className="typing-dots">
        <span /><span /><span />
      </div>
    </div>
  );
}
